import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button, Search } from '@pmi/dsm-react';
import Overlay from '../../../../shared/components/Overlay';
import AdminsList from './AdminsList';
import { useGetAdministratorUsersQuery } from './AdminsList/apiSlice';
import InviteEditAdminModal from './InviteEditAdminModal';
import {
  AdminActions,
  AdministratorUserApiModel,
  BaseAdmin,
  emptyUser,
} from './models';
import { SortedColumn } from '../../../../shared/components/ResponsiveTable/models';
import { accountSelector } from '../../../../shared/store/shared/accountSlice';
import RemoveAdminModal from './RemoveAdminModal';
import styles from '../../../../shared/dsm.module.scss';
import adminStyles from './adminsTab.module.scss';
import PeopleCardList from '../PeopleCardList';
import AdminCardListTemplate from './AdminCardListTemplate';
import FilterButton from '../FilterBy/FilterButton';
import FilterAdmin, { AdminFilters } from './FilterAdmin';
import { useGetAccountDataQuery } from '../../../../shared/navigation/apiSlice';
import { FilterBySelectedItems } from '../FilterBy';
import useBreakpoint, { SIZE_XS } from '../../../../shared/hooks/useBreakpoint';
import usersStyles from '../UsersTab/usersTab.module.scss';
import { JwtDecodedUser } from '../../../../shared/models/UserModels';
import useAdminVisitedPages from '../../../../shared/hooks/useAdminVisitedPages';

interface AdminsTabProps {
  currentUser: JwtDecodedUser;
}

function sortAdminTableData(
  data: AdministratorUserApiModel[],
  fieldName: string,
  sortOrder: number,
) {
  const clone = [...data];

  if (fieldName === 'role') {
    return clone?.sort((a, b) => {
      const aRole = a?.role ? a?.accessType : 'Custom';
      const bRole = b?.role ? b?.accessType : 'Custom';

      // eslint-disable-next-line no-nested-ternary
      return aRole === bRole ? 0 : aRole > bRole ? sortOrder : -sortOrder;
    });
  }

  if (fieldName === 'dateRegistered') {
    const s = sortOrder === 1 ? 'z-pending' : '';
    return clone?.sort((a, b) => {
      const aD =
        a.inviteStatus.toLowerCase() === 'validated' ? a.acceptedDate : s;
      const bD =
        b.inviteStatus.toLowerCase() === 'validated' ? b.acceptedDate : s;
      // eslint-disable-next-line no-nested-ternary
      return aD === bD ? 0 : aD > bD ? sortOrder : -sortOrder;
    });
  }
  if (fieldName === 'invitedByPersonName') {
    const s = sortOrder === 1 ? 'z-invite-dby' : '';
    return clone?.sort((a, b) => {
      const aD = a?.invitedByPersonName
        ? a.invitedByPersonName?.toLowerCase()
        : s;
      const bD = b?.invitedByPersonName
        ? b.invitedByPersonName?.toLowerCase()
        : s;
      // eslint-disable-next-line no-nested-ternary
      return aD === bD ? 0 : aD > bD ? sortOrder : -sortOrder;
    });
  }
  if (fieldName === 'name') {
    const s = sortOrder === 1 ? 'z-name' : '';
    return clone?.sort((a, b) => {
      const aD = a.name ? a.name?.toLowerCase() : s;
      const bD = b.name ? b.name?.toLowerCase() : s;
      // eslint-disable-next-line no-nested-ternary
      return aD === bD ? 0 : aD > bD ? sortOrder : -sortOrder;
    });
  }
  if (fieldName === 'lastLoggedIn') {
    const s = sortOrder === 1 ? 'z-no-date' : '';
    return clone?.sort((a, b) => {
      const aD =
        a.inviteStatus.toLowerCase() === 'validated' ? a?.lastLoggedIn ?? s : s;
      const bD =
        b.inviteStatus.toLowerCase() === 'validated' ? b?.lastLoggedIn ?? s : s;
      // eslint-disable-next-line no-nested-ternary
      return aD === bD ? 0 : aD > bD ? sortOrder : -sortOrder;
    });
  }
  return clone?.sort((a, b) =>
    // eslint-disable-next-line no-nested-ternary
    a[fieldName]?.toString().toLowerCase() ===
    b[fieldName]?.toString().toLowerCase()
      ? 0
      : a[fieldName]?.toString().toLowerCase() >
        b[fieldName]?.toString().toLowerCase()
      ? sortOrder
      : -sortOrder,
  );
}

export default function AdminsTab({ currentUser }: AdminsTabProps) {
  const { currentAccountId } = useSelector(accountSelector);
  const [editableAdmin, setEditableAdmin] = useState<BaseAdmin | undefined>();
  const [removableAdmin, setRemovableAdmin] = useState<BaseAdmin | undefined>();
  const [actions, setActions] = useState<AdminActions>({
    onEdit: setEditableAdmin,
    onRemove: setRemovableAdmin,
    removableAdmin,
  });
  const [sortedColumn, setSortedColumn] = useState<SortedColumn>(null);
  const [searchText, setSearchText] = useState('');
  const [searchResults, setSearchResult] = useState([]);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const bp = useBreakpoint();
  const [filterDetails, setFilterDetails] = useState<{
    isFiltered: boolean;
    selectedFilters: AdminFilters | FilterBySelectedItems;
  }>({
    isFiltered: false,
    selectedFilters: { role: [], dealAccess: [] } as AdminFilters,
  });

  useEffect(() => {
    setActions({ ...actions, removableAdmin });
  }, [removableAdmin]);
  const {
    data: rawAdminList,
    isLoading: loading,
    isFetching: fetching,
  } = useGetAdministratorUsersQuery(currentAccountId);

  const adminList = useMemo(() => {
    if (loading || !rawAdminList) return rawAdminList?.slice();
    if (sortedColumn?.direction) {
      const sortOrder = sortedColumn.direction === 'Asc' ? 1 : -1;
      return sortAdminTableData(rawAdminList, sortedColumn.name, sortOrder);
    }
    return rawAdminList;
  }, [rawAdminList, loading, sortedColumn]);

  const editableAdminFromList = editableAdmin
    ? adminList?.find((a: AdministratorUserApiModel) => {
        if (editableAdmin.personId > 0)
          return a.personId === editableAdmin.personId;
        if (editableAdmin.inviteCode)
          return a.inviteCode === editableAdmin.inviteCode;
        return a.email === editableAdmin.email;
      })
    : null;

  const handleSort = (column: SortedColumn) => {
    setSortedColumn(column);
  };

  const searchAdmin = (text: string) => {
    setSearchText(text);
  };

  const onSearch = (e: any) => {
    const text = e.target as HTMLInputElement;
    searchAdmin(text.value);
  };

  const resetSearch = () => {
    setSearchText('');
    setSearchResult([]);
  };

  const cardTemplate = (item: AdministratorUserApiModel) => (
    <AdminCardListTemplate
      actions={actions}
      item={item}
      personId={currentUser?.person_id}
    />
  );
  const onFilterChange = (
    isFiltered: boolean,
    selectedFilters: AdminFilters | FilterBySelectedItems,
  ) => {
    setFilterDetails({
      isFiltered,
      selectedFilters,
    });
  };

  const doFilter = ({ role = [], dealAccess = [] }: AdminFilters) => {
    const hasRole = (admin: AdministratorUserApiModel) => {
      if (admin?.role) {
        return role.includes(admin.role);
      }
      return false;
    };

    const hasDeal = (admin: AdministratorUserApiModel) => {
      if (admin?.accountRole) {
        return true;
      }
      return false;
    };

    let results: AdministratorUserApiModel[] = [];
    if (role.length > 0 && dealAccess.length === 0) {
      results = adminList.filter((admin) => !!hasRole(admin));
    }
    if (dealAccess.length > 0 && role.length === 0) {
      results = adminList.filter((admin) => hasDeal(admin));
    }
    if (dealAccess.length > 0 && role.length > 0) {
      results = adminList.filter((admin) => hasDeal(admin) && hasRole(admin));
    }
    return results;
  };

  const filterBySearchText = (admin: AdministratorUserApiModel) =>
    admin?.invitationEmail
      ?.toString()
      ?.toLowerCase()
      ?.includes(searchText?.toLowerCase()) ||
    admin?.name
      ?.toString()
      ?.toLowerCase()
      .includes(searchText?.toLowerCase()) ||
    admin?.personId?.toString() === searchText;

  const getAdminList = () => {
    if (!filterDetails.isFiltered && searchText.length > 0) {
      return searchResults;
    }
    if (filterDetails.isFiltered && searchText.length === 0) {
      return doFilter(filterDetails.selectedFilters as AdminFilters);
    }
    if (filterDetails.isFiltered && searchText.length > 0) {
      return doFilter(filterDetails.selectedFilters as AdminFilters)?.filter(
        filterBySearchText,
      );
    }
    return adminList;
  };

  const { data: currentAccountData } = useGetAccountDataQuery(
    currentAccountId,
    {
      skip: !currentAccountId,
    },
  );
  const list = getAdminList();
  const noData =
    !loading &&
    searchText?.length === 0 &&
    !filterDetails.isFiltered &&
    list?.length === 0;
  const isFilterMode =
    list?.length === 0 && searchText?.length === 0 && filterDetails.isFiltered;
  const isSearchMode = list?.length === 0 && searchText?.length > 0;

  const renderTable = () => {
    if (loading) {
      return <p className="py-2">Loading...</p>;
    }
    return (
      <>
        <AdminsList
          list={getAdminList()}
          isLoading={loading}
          handleSort={handleSort}
          actions={actions}
          personId={currentUser?.person_id}
        />
        {noData && (
          <div
            className={`d-block my-5 text-center admin-list-data mx-auto w-75 ${usersStyles.noResult}`}
          >
            No Data to Display...
          </div>
        )}
        {isFilterMode && (
          <div
            className={`d-block my-5 text-center mx-auto w-75 admin-list-data no-users-found ${usersStyles.noResult}`}
          >
            There are no users found.
          </div>
        )}
        {isSearchMode && (
          <div
            className={`d-block my-5 text-center mx-auto admin-list-data w-75 ${adminStyles.noResult}`}
          >
            There are no search results matching{' '}
            <span data-exclude-lg>
              &ldquo;<b>{searchText}</b> &rdquo;.
            </span>
            <br />
            Please check your spelling or try another search.
          </div>
        )}
      </>
    );
  };
  const renderCardList = () => {
    if (loading) {
      return <p className="py-2">Loading...</p>;
    }
    return (
      <>
        <PeopleCardList<AdministratorUserApiModel>
          cardTemplate={cardTemplate}
          items={list}
          loading={loading || fetching}
          paginationProps={{ showPagination: false }}
        />
        {isFilterMode && (
          <div
            className={`d-block my-5 text-center mx-auto w-75 ${usersStyles.noResult}`}
          >
            There are no users found.
          </div>
        )}
        {isSearchMode && (
          <div
            className={`d-block my-5 text-center mx-auto w-75 ${adminStyles.noResult}`}
          >
            There are no search results matching{' '}
            <span data-exclude-lg>
              &ldquo;<b>{searchText}</b> &rdquo;.
            </span>
            <br />
            Please check your spelling or try another search.
          </div>
        )}
      </>
    );
  };

  const visit = useAdminVisitedPages('people.admins');

  useEffect(() => {
    if (!loading && !fetching && adminList && searchText.length > 0) {
      setSearchResult(adminList);
      const result = adminList.filter(filterBySearchText);
      setSearchResult(result);
    }
  }, [searchText]);

  useEffect(() => {
    if (!loading && !fetching && !visit.isLoading) {
      visit.performFirstTimeVisit('.admin-list-data');
    }
  }, [loading, visit.isLoading]);

  return (
    <div className={adminStyles.people__listTabCard_adminsTab}>
      <div className="d-flex flex-column flex-sm-row my-3 w-100">
        <div className="people-admins_search-filter d-sm-flex d-block">
          <Search
            id="adminSearch"
            placeholder="Search by name or email address"
            className={`${styles.secondarySearch} me-0 me-sm-3`}
            onChange={onSearch}
            onClear={resetSearch}
            value={searchText}
            style={{ width: 330 }}
          />
          <div className="d-flex">
            <FilterButton onOpen={(open) => setIsFilterOpen(open)} />
          </div>
        </div>

        <div className="flex-grow-1 d-flex justify-content-sm-end justify-content-start">
          <Button
            className="people-admins_invite_admin-btn"
            titleText="Invite Admin"
            size="md"
            type="button"
            onClick={() => setEditableAdmin(emptyUser)}
          />
        </div>
      </div>

      {!loading && (
        <FilterAdmin
          show={isFilterOpen}
          onFilter={onFilterChange}
          currentAccountData={currentAccountData}
        />
      )}
      {bp !== SIZE_XS && renderTable()}
      {bp === SIZE_XS && renderCardList()}

      {/* modals and overlays */}
      {editableAdmin && (
        <InviteEditAdminModal
          admin={{
            ...editableAdmin,
            phone: editableAdminFromList?.phone,
            phoneNumber: editableAdminFromList?.phoneNumber,
          }}
          onCancel={() => setEditableAdmin(undefined)}
        />
      )}
      {removableAdmin && (
        <RemoveAdminModal
          admin={removableAdmin}
          onCancel={() => setRemovableAdmin(undefined)}
        />
      )}
      <Overlay
        variant="PMIOverlay"
        global
        animated
        fade
        iconColor="brand-dark"
        overlayColor="black"
        overlayOpacity={1}
        show={loading || fetching || visit.isLoading}
      />
    </div>
  );
}
